import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Mail, Sparkles } from 'lucide-react';
import { EMAIL_REDIRECT_TO } from '../lib/authRedirect';

const PasswordResetRequest = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase) {
      setMessage('Supabase クライアントが初期化されていません。環境変数をご確認ください。');
      setMessageType('error');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    setMessageType(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: EMAIL_REDIRECT_TO
      });
      if (error) {
        setMessage(error.message);
        setMessageType('error');
      } else {
        setMessage(
          'パスワード再設定用のメールを送信しました。\n登録されていないメールには届きません。届かない場合は管理者に確認してください。'
        );
        setMessageType('success');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <header className="bg-gray-800 px-6 py-4 text-white shadow">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500 shadow-inner">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-lg font-semibold tracking-wide">勤怠管理システム</p>
              <p className="text-xs text-blue-100">パスワード再設定</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <ArrowLeft className="h-4 w-4" />
            ログインへ戻る
          </button>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl ring-1 ring-gray-100">
          <h1 className="text-2xl font-semibold text-gray-900">パスワード再設定</h1>
          <p className="mt-2 text-sm text-gray-600">
            登録済みのメールアドレスを入力すると、再設定用リンクをお送りします。
          </p>

          {message && (
            <div
              className={[
                'mt-6 rounded-lg border px-4 py-3 text-sm whitespace-pre-line',
                messageType === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : messageType === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
              ].join(' ')}
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <label className="block text-left">
              <span className="text-sm font-medium text-gray-700">メールアドレス</span>
              <div className="relative mt-2">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                  placeholder="email@example.com"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-10 py-3 text-gray-900 shadow-sm transition focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              再設定メールを送信
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PasswordResetRequest;
